import { useTu } from "@src/appV2/i18n/useTu";
import { isDefined, isEmpty } from "@clipboard-health/util-ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, Autocomplete, CircularProgress, MenuItem, Stack, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useFetchWorkplaceWorkerTypes } from "@src/appV2/FacilityDrawer/api/useFetchWorkplaceWorkerTypes";
import type { TuFunction } from "@src/appV2/i18n/types";
import { useDebounce } from "@src/appV2/lib/utils";
import { getQualificationsAvailableToDisplay } from "@src/appV2/Shifts/utils/getQualificationsAvailableToDisplay";
import { type Facility, useSearchFacilities } from "@src/appV2/User/api/useSearchFacilities";
import { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useGetFacilityProfile } from "../../../FacilityProfile/api/useGetFacilityProfile";
import { Text } from "../../components/Text";
import { formatAgentRequestLabel } from "../../DailyView/utils/formatAgentRequestLabel";

import {
  type ShiftMigrationDestinationWorkplace,
  type ShiftMigrationOriginWorkplace,
} from "../types";

import { fromWorkplaceDayPickerValue, toWorkplaceDayPickerValue } from "../utils/workplaceDay";

import {
  getParametersFormSchema,
  type ShiftMigrationParametersFormValues,
  type ShiftMigrationParametersSubmitValues,
} from "./shiftMigrationParametersForm";

interface ShiftMigrationParametersStepProps {
  origin: ShiftMigrationOriginWorkplace;
  defaults?: Partial<ShiftMigrationParametersFormValues>;
  isSubmitting: boolean;
  onSubmit: (values: ShiftMigrationParametersSubmitValues) => void;
  formId: string;
}

function getNoOptionsText(searchInput: string, isLoading: boolean, tu: TuFunction): string {
  if (isEmpty(searchInput)) {
    return tu("Start typing to search for workplaces");
  }
  return isLoading ? tu("Searching workplaces...") : tu("No workplaces found");
}

export function ShiftMigrationParametersStep(props: ShiftMigrationParametersStepProps) {
  const { tu } = useTu("shiftMigration");

  const { origin, defaults, isSubmitting, onSubmit, formId } = props;
  const parametersFormSchema = getParametersFormSchema(tu);
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce({ debounceTimeInMs: 300, value: searchInput });

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ShiftMigrationParametersFormValues>({
    resolver: zodResolver(parametersFormSchema),
    defaultValues: {
      destination: defaults?.destination ?? null,
      effectiveStarting: defaults?.effectiveStarting,
      effectiveEnding: defaults?.effectiveEnding ?? null,
      qualification: defaults?.qualification ?? "",
    },
    mode: "onChange",
  });

  const { data: facilitiesData, isFetching: facilitiesLoading } = useSearchFacilities(
    { searchInput: debouncedSearch },
    { enabled: debouncedSearch.length > 0 }
  );

  const destinationOptions: ShiftMigrationDestinationWorkplace[] =
    facilitiesData?.pages
      .flatMap((page) => page.list)
      .filter((facility: Facility) => facility.userId !== origin.id)
      .map((facility: Facility) => ({ id: facility.userId, name: facility.name })) ?? [];

  const watchedDestination = watch("destination");

  const { data: destinationProfile } = useGetFacilityProfile(
    watchedDestination?.id ?? "",
    tu("name,userId,fullAddress"),
    { enabled: isDefined(watchedDestination?.id) }
  );

  const destinationAddress = destinationProfile?.fullAddress?.formatted;

  const addressMismatch =
    isDefined(destinationAddress) &&
    isDefined(origin.address) &&
    origin.address.trim().toLowerCase() !== destinationAddress.trim().toLowerCase();

  // Qualifications enabled for the origin facility, derived the same way the
  // calendar / Daily View builds its qualification tabs: visible worker types
  // that are available to display (i.e. contracted). See DailyViewContainer.
  const { data: workerTypesData } = useFetchWorkplaceWorkerTypes(
    { workplaceId: origin.id, showHidden: true },
    { enabled: isDefined(origin.id) }
  );

  const qualificationOptions = useMemo(() => {
    const workerTypes = workerTypesData?.data.workerTypes ?? [];
    const available = getQualificationsAvailableToDisplay(workerTypes);
    const names = new Set(
      workerTypes
        .filter((workerType) => workerType.visible && available.has(workerType.name))
        .map((workerType) => workerType.name)
    );
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [workerTypesData]);

  return (
    <form
      id={formId}
      onSubmit={handleSubmit((values) => {
        if (!isDefined(values.destination)) {
          return;
        }
        onSubmit({
          destination: values.destination,
          effectiveStarting: values.effectiveStarting,
          effectiveEnding: values.effectiveEnding ?? undefined,
          qualification: isEmpty(values.qualification) ? undefined : values.qualification,
        });
      })}
    >
      <Stack spacing={4}>
        <Stack spacing={1}>
          <Text semibold variant="body2">
            {tu("Origin workplace")}
          </Text>
          <Text variant="body2">{origin.name}</Text>
          <Text sx={(theme) => ({ color: theme.palette.text.secondary })} variant="caption">
            {origin.address ?? tu("Address unavailable")}
          </Text>
        </Stack>

        <Stack spacing={1}>
          <Text semibold variant="body2">
            {tu("Destination workplace")}
          </Text>
          <Controller
            control={control}
            name="destination"
            render={({ field }) => (
              <Autocomplete
                autoComplete
                disabled={isSubmitting}
                filterOptions={(options) => options}
                getOptionKey={(option) => (typeof option === "string" ? option : option.id)}
                getOptionLabel={(option) => (typeof option === "string" ? option : option.name)}
                isOptionEqualToValue={(option, value) => {
                  if (typeof option === "string" || typeof value === "string") {
                    return false;
                  }
                  return option.id === value.id;
                }}
                loading={facilitiesLoading}
                loadingText={tu("Searching workplaces...")}
                noOptionsText={getNoOptionsText(searchInput, facilitiesLoading, tu)}
                options={destinationOptions}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={isDefined(errors.destination)}
                    helperText={errors.destination?.message}
                    placeholder={tu("Start typing workplace name...")}
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {facilitiesLoading && <CircularProgress color="inherit" size={20} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(renderProps, option) => {
                  if (!option) {
                    return null;
                  }
                  return (
                    <li {...renderProps} key={option.id}>
                      <Stack direction="column">
                        <Text variant="body2">{option.name}</Text>
                        <Text
                          sx={(theme) => ({ color: theme.palette.text.secondary })}
                          variant="caption"
                        >
                          {tu("ID: {{id}}", { values: { id: option.id } })}
                        </Text>
                      </Stack>
                    </li>
                  );
                }}
                value={field.value}
                onChange={(_, newValue) => {
                  if (typeof newValue !== "string") {
                    field.onChange(newValue);
                  }
                }}
                onInputChange={(_, newInputValue) => {
                  setSearchInput(newInputValue);
                }}
              />
            )}
          />
          {isDefined(destinationAddress) && (
            <Text sx={(theme) => ({ color: theme.palette.text.secondary })} variant="caption">
              {destinationAddress}
            </Text>
          )}
          {addressMismatch && (
            <Alert severity="warning">
              {tu(
                "Origin and destination addresses don't match. The backend will reject the migration unless the addresses are compatible."
              )}
            </Alert>
          )}
        </Stack>

        <Stack direction="row" spacing={3}>
          <Controller
            control={control}
            name="effectiveStarting"
            render={({ field }) => (
              <DatePicker
                disabled={isSubmitting}
                label={tu("Effective starting")}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    error: isDefined(errors.effectiveStarting),
                    helperText: errors.effectiveStarting?.message,
                  },
                }}
                value={toWorkplaceDayPickerValue(field.value, origin.tmz)}
                onChange={(date) => {
                  field.onChange(fromWorkplaceDayPickerValue(date, origin.tmz) ?? undefined);
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="effectiveEnding"
            render={({ field }) => (
              <DatePicker
                disabled={isSubmitting}
                label={tu("Effective ending (optional)")}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    error: isDefined(errors.effectiveEnding),
                    helperText: errors.effectiveEnding?.message,
                  },
                }}
                value={toWorkplaceDayPickerValue(field.value, origin.tmz)}
                onChange={(date) => {
                  field.onChange(fromWorkplaceDayPickerValue(date, origin.tmz));
                }}
              />
            )}
          />
        </Stack>

        <Stack spacing={1}>
          <Text semibold variant="body2">
            {tu("Qualification")}
          </Text>
          <Controller
            control={control}
            name="qualification"
            render={({ field }) => (
              <TextField
                select
                disabled={isSubmitting}
                helperText={tu(
                  "Limit the migration to a single qualification, or migrate every qualification."
                )}
                name={field.name}
                size="small"
                value={field.value ?? ""}
                onChange={field.onChange}
              >
                <MenuItem value="">{tu("Any qualification")}</MenuItem>
                {qualificationOptions.map((qualification) => (
                  <MenuItem key={qualification} value={qualification}>
                    {formatAgentRequestLabel(qualification)}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Stack>
      </Stack>
    </form>
  );
}
